import {useContext, useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {AccountType} from '../../../components/Search/SearchNav/types';
import {envIsPublicCloud} from '../../../config';
import {
  useFetchArtifactCollectionSupplementalQuery,
  useFetchRegistryProjectsPartialQuery,
  useOrganizationReadOnlyQuery,
  useProjectReadOnlyQuery,
  useRegistryProjectQuery,
} from '../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {
  useRampFlagSandboxes,
  useStatsigGateDeployFromRegistry,
  useStatsigGateDeployFromRegistryWithLoading,
  useStatsigGateSourceArtifactProtection,
} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {
  FETCH_REGISTRIES_PAGE_SIZE,
  useRegistriesWithMembership,
} from '../RegistriesHomepage/hooks';
import {getAllRegistriesSearchResult} from '../RegistriesHomepage/utils';
import {StarredRegistriesContext} from './StarredRegistriesContextProvider';
import {
  checkRegistryProject,
  DEFAULT_LANDING_PAGE_TAB,
  getGlobalRegistryType,
  isEmptyRegistryLanding,
  type LandingPageTab,
  parseLandingTab,
  REGISTRY_PROJECT_PREFIX,
} from './utils';

// Keeping this hook here till we deprecate the model registry for personal entities on SaaS and Server.
export const useIsGlobalRegistryEnabled = (
  entityName?: string,
  isEntityNameLoading?: boolean
): {
  enableGlobalRegistry: boolean;
  enableGlobalRegistryLoading: boolean;
} => {
  const {selectedAccount} = useContext(AccountSelectorContext);

  const isPersonalAccountOnSaaS =
    selectedAccount?.accountType === AccountType.Personal && envIsPublicCloud;
  const viewer = useViewer();
  const isPersonalEntity =
    entityName != null && viewer?.username === entityName;
  return useMemo(() => {
    if (isPersonalAccountOnSaaS || isPersonalEntity) {
      return {
        enableGlobalRegistry: false,
        enableGlobalRegistryLoading: isEntityNameLoading ?? false,
      };
    }
    return {
      enableGlobalRegistry: true,
      enableGlobalRegistryLoading: false,
    };
  }, [isPersonalEntity, isEntityNameLoading, isPersonalAccountOnSaaS]);
};

export const useIsOrgReadOnly = (): {
  isOrganizationReadOnly: boolean;
  orgId: string;
  orgName: string;
  entityName: string;
  loading: boolean;
  advancedUI: boolean;
  advancedSearch: boolean;
} => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {data: organizationData, loading: organizationLoading} =
    useOrganizationReadOnlyQuery({
      variables: {orgName},
      context: propagateErrorsContext(),
      skip: !orgName,
    });

  const state = useMemo(
    () => ({
      isOrganizationReadOnly:
        organizationData?.organization?.orgEntity?.readOnly ?? true,
      orgId: organizationData?.organization?.id ?? '',
      orgName,
      entityName: organizationData?.organization?.orgEntity?.name ?? '',
      loading: organizationLoading || !orgName,
      advancedUI:
        organizationData?.organization?.advancedRegistryFeatures?.advancedUI ??
        false,
      advancedSearch:
        organizationData?.organization?.advancedRegistryFeatures
          ?.advancedSearch ?? false,
    }),
    [organizationData, organizationLoading, orgName]
  );
  return state;
};

export const useIsProjectReadOnly = (
  entityName: string,
  projectName: string
): {isProjectReadOnly: boolean; loading: boolean} => {
  const {data, loading} = useProjectReadOnlyQuery({
    variables: {entityName, projectName},
    context: propagateErrorsContext(),
    skip: entityName === '' || projectName === '',
  });

  const state = useMemo(
    () => ({
      isProjectReadOnly: data?.entity?.project?.readOnly ?? true,
      loading,
    }),
    [data, loading]
  );
  return state;
};

export const useRegistryProject = (orgName: string, projectName?: string) => {
  const fullProjectName = `${REGISTRY_PROJECT_PREFIX}${projectName}`;

  const {data, loading, error} = useRegistryProjectQuery({
    variables: {orgName, projectName: fullProjectName},
    context: propagateErrorsContext(),
    skip: !orgName || !projectName,
  });

  const registryProject = useMemo(() => {
    const entityName = data?.organization?.orgEntity?.name ?? '';
    const orgId = data?.organization?.id ?? '';
    if (data?.organization?.orgEntity?.project != null && projectName != null) {
      return getGlobalRegistryType(
        data?.organization?.orgEntity?.project,
        projectName,
        entityName,
        orgId,
        orgName
      );
    }
    return undefined;
  }, [data, projectName, orgName]);

  return {
    data: registryProject,
    loading,
    error,
  };
};

export const useFetchArtifactCollectionSupplemental = (
  collectionId: string
) => {
  const {data, loading, error} = useFetchArtifactCollectionSupplementalQuery({
    variables: {collectionId},
    context: propagateErrorsContext(),
  });

  const numConsumers = useMemo(() => {
    const consumers = data?.artifactCollection?.artifacts?.edges.flatMap(edge =>
      edge.node.usedBy.edges.map(usedBy => usedBy.node.user?.name)
    );
    return new Set(consumers).size;
  }, [data]);

  return {
    isProjectDeleted: !data?.artifactCollection?.project,
    numVersions: data?.artifactCollection?.artifacts?.totalCount ?? 0,
    numConsumers,
    loading,
    error,
  };
};

export const useIsSourceArtifactProtectionEnabled = () => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  return useStatsigGateSourceArtifactProtection(orgName);
};

export const useRegistrySetup = () => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  // Initial fetch to setup default registries if they don't exist
  const {data: registries, loading} = useFetchRegistryProjectsPartialQuery({
    variables: {
      orgName,
      limit: 1,
    },
    skip: !orgName,
  });

  const isRegistriesLoading = loading || !orgName;

  return {
    orgName,
    registries,
    isRegistriesLoading,
  };
};

export const useRegistryOrgContext = () => {
  const viewer = useViewer();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const {
    isOrganizationReadOnly: readOnly,
    orgId,
    entityName,
    loading: isOrganizationReadOnlyLoading,
    advancedUI,
    advancedSearch,
  } = useIsOrgReadOnly();

  return {
    viewer,
    userId: viewer?.id,
    userName: viewer?.username,
    orgName,
    readOnly,
    orgId,
    entityName,
    loading: isOrganizationReadOnlyLoading,
    advancedUI,
    advancedSearch,
  };
};

/** Registry Deploy CTAs require both the live Sandboxes product and the Registry deploy gate. */
export const useIsDeployFromRegistryEnabled = () => {
  const {orgId} = useRegistryOrgContext();
  const sandboxesEnabled = useRampFlagSandboxes(orgId);
  const deployFromRegistryEnabled = useStatsigGateDeployFromRegistry(orgId);
  return Boolean(sandboxesEnabled && deployFromRegistryEnabled);
};

export const useIsEmptyRegistryLanding = (): {
  isEmpty: boolean;
  loading: boolean;
} => {
  const {orgId, orgName, loading: orgLoading} = useRegistryOrgContext();
  const sandboxesEnabled = useRampFlagSandboxes(orgId);
  const {enabled: deployEnabled, loading: flagLoading} =
    useStatsigGateDeployFromRegistryWithLoading(orgId);
  const enabled = Boolean(sandboxesEnabled && deployEnabled);
  const {data, loading: registriesLoading} =
    useFetchRegistryProjectsPartialQuery({
      variables: {
        orgName,
        pattern: REGISTRY_PROJECT_PREFIX,
        limit: FETCH_REGISTRIES_PAGE_SIZE,
      },
      skip: !orgName || (!flagLoading && !enabled),
      context: propagateErrorsContext(),
      notifyOnNetworkStatusChange: true,
    });

  return useMemo(() => {
    if (flagLoading || orgLoading) {
      return {isEmpty: false, loading: true};
    }
    if (!enabled) {
      return {isEmpty: false, loading: false};
    }
    if (registriesLoading) {
      return {isEmpty: false, loading: true};
    }
    const connection = getAllRegistriesSearchResult(data);
    const registries = (connection?.edges ?? []).flatMap(edge => {
      const node = edge?.node;
      if (node == null) {
        return [];
      }
      const {registryName} = checkRegistryProject(node.name);
      if (registryName == null) {
        return [];
      }
      return [
        {
          registryName,
          collectionCount: node.totalArtifactCollections,
        },
      ];
    });
    return {
      isEmpty: isEmptyRegistryLanding(
        registries,
        connection?.pageInfo.hasNextPage ?? false
      ),
      loading: false,
    };
  }, [data, enabled, flagLoading, orgLoading, registriesLoading]);
};

export const useStarredRegistries = () => {
  const {orgName, userId} = useRegistryOrgContext();

  const {
    starredRegistries: starredRegistriesMap,
    isLoading: isStarredRegistriesLoading,
  } = useContext(StarredRegistriesContext);

  const starredRegistries = useMemo(
    () => Object.values(starredRegistriesMap),
    [starredRegistriesMap]
  );

  const {registriesWithMembership: starredRegistriesWithMembership} =
    useRegistriesWithMembership({
      orgName,
      registries: starredRegistries,
      viewerId: userId,
    });

  return {
    starredRegistriesWithMembership,
    isStarredRegistriesLoading,
  };
};

export const useLandingPageTab = () => {
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const tab = tabParam ? parseLandingTab(tabParam) : DEFAULT_LANDING_PAGE_TAB;

  const setTab = (newTab: LandingPageTab, baseUrl?: string) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('tab', newTab);
    const url = baseUrl ?? location.pathname;
    history.push(`${url}?${newSearchParams.toString()}`);
  };

  return [tab, setTab] as const;
};
